import React, { useEffect, useState } from "react";
import Layout from "./Layout.jsx";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, NavLink } from "react-router-dom";
import { getMe } from "../features/authSlice.js";
import axios from "axios";
import {
  HiClipboardList,
  HiCheckCircle,
  HiCube,
  HiLightningBolt,
  HiDocumentText,
  HiBadgeCheck,
  HiChartBar,
  HiArrowRight,
  HiShieldCheck,
  HiInboxIn,
} from "react-icons/hi";

const SHORTCUTS = [
  {
    key: "orders.list",
    label: "Order List",
    icon: HiClipboardList,
    color: "from-blue-500 to-blue-700",
    to: "/orderlist",
  },
  {
    key: "orders.qc",
    label: "QC Checking",
    icon: HiCheckCircle,
    color: "from-teal-500 to-teal-700",
    to: "/qc_checking",
  },
  {
    key: "prod.req",
    label: "Request Material",
    icon: HiLightningBolt,
    color: "from-orange-500 to-orange-700",
    to: "/request_mat",
  },
  {
    key: "store.current",
    label: "Current Stock",
    icon: HiCube,
    color: "from-purple-500 to-purple-700",
    to: "/currentstock",
  },
  {
    key: "store.tx",
    label: "Transactions",
    icon: HiChartBar,
    color: "from-pink-500 to-pink-700",
    to: "/transactions",
  },
  {
    key: "orders.qc_shipment",
    label: "QC Shipments",
    icon: HiBadgeCheck,
    color: "from-green-500 to-green-700",
    to: "/qc_shipment_list",
  },
  {
    key: "jobsheet.search",
    label: "Jobsheet",
    icon: HiDocumentText,
    color: "from-indigo-500 to-indigo-700",
    to: "/jobsheet",
  },
  {
    key: "prod.reqList",
    label: "Material Requests",
    icon: HiClipboardList,
    color: "from-yellow-500 to-yellow-600",
    to: "/request-list",
  },
  {
    key: "store.approve",
    label: "Approve Material",
    icon: HiShieldCheck,
    color: "from-rose-500 to-rose-700",
    to: "/approve_mat",
  },
  {
    key: "store.approve_stockin",
    label: "Approve Stock In",
    icon: HiInboxIn,
    color: "from-cyan-500 to-cyan-700",
    to: "/approve_stockin",
  },
];

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function StatCard({ label, value, sub, color }) {
  return (
    <div
      className={`rounded-xl p-4 text-white bg-gradient-to-br ${color} shadow-md`}
    >
      <p className="text-xs font-semibold uppercase tracking-wide opacity-80">
        {label}
      </p>
      <p className="text-3xl font-bold mt-1">
        {value === null ? <span className="text-lg opacity-50">—</span> : value}
      </p>
      {sub && <p className="text-xs mt-1 opacity-70">{sub}</p>}
    </div>
  );
}

function DashboardContent({ user, permissions }) {
  const [stats, setStats] = useState({ onProcess: null, qcCheck: null });

  useEffect(() => {
    axios
      .get("https://eps.elkomprintama.com/get/api/orders/qc")
      .then((res) => {
        const orders = res.data.result || [];
        setStats({
          onProcess: orders.filter((o) => o.job_status === "ON PROCESS").length,
          qcCheck: orders.filter((o) => o.job_status === "QC CHECK").length,
        });
      })
      .catch(() => {});
  }, []);

  const roleLabel = user?.level || `Level ${user?.user_lvl}`;
  const today = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const isAdmin = user?.user_lvl === 1 || permissions === "admin";
  const permSet = isAdmin
    ? null
    : Array.isArray(permissions)
      ? new Set(permissions)
      : null;

  const visibleShortcuts = SHORTCUTS.filter(
    (s) => !permSet || permSet.has(s.key),
  );
  const showStats =
    isAdmin ||
    !permSet ||
    permSet.has("orders.list") ||
    permSet.has("orders.qc");

  return (
    <div className="max-w-5xl mx-auto space-y-6 py-2">
      {/* Welcome banner */}
      <div
        className="rounded-2xl p-6 text-white shadow-lg relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #0f2027 0%, #203a43 60%, #2c5364 100%)",
        }}
      >
        <div
          className="absolute -right-12 -top-12 w-48 h-48 rounded-full opacity-10"
          style={{
            background: "radial-gradient(circle, #04befe, transparent)",
          }}
        />
        <div
          className="absolute -right-4 bottom-0 w-32 h-32 rounded-full opacity-10"
          style={{
            background: "radial-gradient(circle, #4481eb, transparent)",
          }}
        />
        <p className="text-blue-300 text-xs font-semibold uppercase tracking-widest mb-1">
          {getGreeting()}
        </p>
        <h1 className="text-2xl font-bold">{user?.username}</h1>
        <div className="flex items-center gap-2 mt-1">
          <span className="bg-blue-500/30 text-blue-200 text-xs px-2 py-0.5 rounded-full font-semibold">
            {roleLabel}
          </span>
          <span className="text-gray-400 text-xs">{today}</span>
        </div>
      </div>

      {/* Stats */}
      {showStats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard
            label="On Process"
            value={stats.onProcess}
            sub="Active orders"
            color="from-blue-500 to-blue-700"
          />
          <StatCard
            label="QC Queue"
            value={stats.qcCheck}
            sub="Awaiting QC check"
            color="from-teal-500 to-teal-700"
          />
          <StatCard
            label="Total Active"
            value={
              stats.onProcess !== null && stats.qcCheck !== null
                ? stats.onProcess + stats.qcCheck
                : null
            }
            sub="On process + QC"
            color="from-indigo-500 to-indigo-700"
          />
          <div className="rounded-xl p-4 bg-white shadow-md border border-gray-100 flex flex-col justify-between">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
              System
            </p>
            <div>
              <p className="text-sm font-bold text-gray-700 mt-1">EPS v1.0</p>
              <p className="text-xs text-gray-400 mt-0.5">
                PT. Elkom Printama Batam
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick access */}
      {visibleShortcuts.length > 0 && (
        <div>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
            Quick Access
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {visibleShortcuts.map((s) => {
              const Icon = s.icon;
              return (
                <NavLink
                  key={s.key}
                  to={s.to}
                  className={`group flex flex-col items-start p-4 rounded-xl bg-gradient-to-br ${s.color} text-white shadow hover:shadow-lg hover:scale-[1.02] transition-all duration-150`}
                >
                  <Icon className="text-xl mb-2 opacity-80" />
                  <span className="text-xs font-bold leading-tight">
                    {s.label}
                  </span>
                  <HiArrowRight className="mt-2 text-xs opacity-0 group-hover:opacity-70 transition-opacity -translate-x-1 group-hover:translate-x-0 duration-150" />
                </NavLink>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isError, user, permissions } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(getMe());
  }, [dispatch]);

  useEffect(() => {
    if (isError) navigate("/");
  }, [isError, navigate]);

  return (
    <Layout>
      {user && <DashboardContent user={user} permissions={permissions} />}
    </Layout>
  );
};

export default Dashboard;
